import React, { useEffect, useMemo, useState } from 'react';
import SettingsLayout from '@/components/settings/SettingsLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/supabaseClient';
import { hasCap } from '@/lib/capabilities';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { useSettingsHierarchy } from '@/lib/settingsHierarchy';

const WEEKDAYS = [
  'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì',
  'Venerdì', 'Sabato', 'Domenica',
];

function emptySlot() {
  return { start_time: '', end_time: '', break_minutes: 0, active: false, exists: false };
}

function slotKey(weekday, serviceId) {
  return `${weekday}:${serviceId}`;
}

export default function ImpostazioniOrariTurni() {
  const me = useEffectiveMe();
  const canEdit = hasCap(me, 'turni.edit');
  const {
    companies, locations, locationsByCompany, departmentsByLocation,
    loading: hierarchyLoading, loadingDepartments, error: hierarchyError,
    loadDepartments,
  } = useSettingsHierarchy({ eagerDepartments: true });
  const [companyId, setCompanyId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [serviceCode, setServiceCode] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [serviceSaving, setServiceSaving] = useState(false);
  const [serviceMessage, setServiceMessage] = useState('');
  const [serviceError, setServiceError] = useState('');

  const visibleLocations = useMemo(
    () => locationsByCompany[companyId] || [],
    [locationsByCompany, companyId],
  );
  const visibleDepartments = useMemo(
    () => departmentsByLocation[locationId] || [],
    [departmentsByLocation, locationId],
  );
  const schedulingServices = useMemo(
    () => services.filter(service => service.active !== false),
    [services],
  );
  const canManageServices = canEdit && !me.is_global && (me.role_codes || []).includes('TITOLARE');

  useEffect(() => {
    if (!companyId && companies[0]) setCompanyId(companies[0].id);
  }, [companies, companyId]);

  useEffect(() => {
    if (companyId && !visibleLocations.some(location => location.id === locationId)) {
      setLocationId(visibleLocations[0]?.id || '');
    }
  }, [companyId, locationId, visibleLocations]);

  useEffect(() => {
    if (locationId && !departmentsByLocation[locationId]) loadDepartments(locationId);
  }, [locationId, departmentsByLocation, loadDepartments]);

  useEffect(() => {
    if (locationId && !visibleDepartments.some(department => department.id === departmentId)) {
      setDepartmentId(visibleDepartments[0]?.id || '');
    }
  }, [locationId, departmentId, visibleDepartments]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSlots({});
    Promise.all([
      apiFetch('/api/turni/services'),
      apiFetch('/api/turni/default-schedules'),
    ]).then(([serviceRows, scheduleRows]) => {
      if (cancelled) return;
      setServices(serviceRows || []);
      const next = {};
      (scheduleRows || []).forEach((row) => {
        if (row.department_id !== departmentId) return;
        next[slotKey(row.weekday, row.service_id)] = {
          start_time: row.start_time.slice(0, 5),
          end_time: row.end_time.slice(0, 5),
          break_minutes: row.break_minutes || 0,
          active: Boolean(row.active),
          exists: true,
        };
      });
      setSlots(next);
      setError('');
    }).catch((loadError) => {
      if (!cancelled) setError(loadError.message || 'Impossibile caricare gli orari predefiniti');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [departmentId]);

  const updateSlot = (weekday, serviceId, field, value) => {
    const key = slotKey(weekday, serviceId);
    setSlots(current => ({
      ...current,
      [key]: { ...emptySlot(), ...(current[key] || {}), [field]: value, exists: true },
    }));
    setMessage('');
  };

  const save = async () => {
    if (!canEdit || !companyId || !locationId || !departmentId) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const items = Object.entries(slots)
        .filter(([, slot]) => slot.exists && (slot.start_time || slot.end_time))
        .map(([key, slot]) => {
          const [weekday, serviceId] = key.split(':');
          return {
            company_id: companyId,
            location_id: locationId,
            department_id: departmentId,
            weekday: Number(weekday),
            service_id: serviceId,
            start_time: slot.start_time,
            end_time: slot.end_time,
            break_minutes: Number(slot.break_minutes) || 0,
            active: Boolean(slot.active),
          };
        });
      await apiFetch('/api/turni/default-schedules', {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
      setMessage('Orari predefiniti salvati.');
    } catch (saveError) {
      setError(saveError.message || 'Impossibile salvare gli orari predefiniti');
    } finally {
      setSaving(false);
    }
  };

  const createService = async (event) => {
    event.preventDefault();
    if (!canManageServices) return;
    setServiceSaving(true);
    setServiceError('');
    setServiceMessage('');
    try {
      const created = await apiFetch('/api/turni/services', {
        method: 'POST',
        body: JSON.stringify({
          code: serviceCode.trim().toUpperCase(),
          name: serviceName.trim(),
        }),
      });
      setServices(current => [...current, created]);
      setServiceCode('');
      setServiceName('');
      setServiceMessage(`Servizio ${created.name} aggiunto al tenant.`);
    } catch (createError) {
      setServiceError(createError.message || 'Impossibile creare il servizio');
    } finally {
      setServiceSaving(false);
    }
  };

  const selectedDepartment = visibleDepartments.find(item => item.id === departmentId);

  return (
    <SettingsLayout
      eyebrow="Impostazioni · Turni"
      title="Orari predefiniti Turni"
      description="Definisci gli orari proposti quando crei un nuovo turno. I turni già salvati non vengono ricalcolati."
      breadcrumbs={[{ label: 'Impostazioni', to: '/impostazioni' }]}
      current="Orari predefiniti Turni"
      navigation={[
        { to: '/impostazioni/struttura-aziendale', label: 'Struttura aziendale' },
        { to: '/turni', label: 'Turni e copertura' },
      ]}
      testId="impostazioni-orari-turni-page"
    >
      <div className="space-y-5">
        <Card>
          <CardHeader>
            <CardTitle>Perimetro organizzativo</CardTitle>
            <CardDescription>Ogni combinazione di azienda, locale e reparto mantiene la propria configurazione.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="default-shifts-company">Azienda</Label>
              <select id="default-shifts-company" className="h-10 w-full rounded-xl border border-app-border bg-white px-3 text-sm" value={companyId} onChange={event => setCompanyId(event.target.value)}>
                <option value="">Seleziona azienda</option>
                {companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="default-shifts-location">Locale</Label>
              <select id="default-shifts-location" className="h-10 w-full rounded-xl border border-app-border bg-white px-3 text-sm" value={locationId} onChange={event => setLocationId(event.target.value)} disabled={!companyId}>
                <option value="">Seleziona locale</option>
                {visibleLocations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="default-shifts-department">Reparto</Label>
              <select id="default-shifts-department" className="h-10 w-full rounded-xl border border-app-border bg-white px-3 text-sm" value={departmentId} onChange={event => setDepartmentId(event.target.value)} disabled={!locationId || Boolean(loadingDepartments[locationId])}>
                <option value="">Seleziona reparto</option>
                {visibleDepartments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
              </select>
            </div>
          </CardContent>
        </Card>

        {canManageServices && (
          <Card data-testid="turni-services-card">
            <CardHeader>
              <CardTitle>Servizi Turni</CardTitle>
              <CardDescription>
                Il catalogo è condiviso nel tenant; gli orari predefiniti restano configurabili per locale e reparto.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {schedulingServices.length > 0 ? (
                <ul className="flex flex-wrap gap-2" aria-label="Servizi attivi">
                  {schedulingServices.map(service => (
                    <li key={service.id} className="rounded-full border border-app-border bg-stone-50 px-3 py-1.5 text-sm">
                      <span className="font-medium">{service.name}</span>
                      <span className="ml-2 text-stone-500">{service.code}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-stone-600">Non ci sono ancora servizi attivi nel tenant.</p>
              )}
              <form data-testid="turni-service-form" onSubmit={createService} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                <div className="space-y-1.5">
                  <Label htmlFor="turni-service-code">Codice</Label>
                  <Input
                    id="turni-service-code"
                    value={serviceCode}
                    onChange={event => setServiceCode(event.target.value.toUpperCase())}
                    maxLength={40}
                    pattern="[A-Z0-9_-]+"
                    placeholder="PRANZO"
                    required
                    disabled={serviceSaving}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="turni-service-name">Nome servizio</Label>
                  <Input
                    id="turni-service-name"
                    value={serviceName}
                    onChange={event => setServiceName(event.target.value)}
                    maxLength={80}
                    placeholder="Pranzo"
                    required
                    disabled={serviceSaving}
                  />
                </div>
                <Button type="submit" disabled={serviceSaving || !serviceCode.trim() || !serviceName.trim()}>
                  {serviceSaving ? 'Creazione…' : 'Aggiungi servizio'}
                </Button>
              </form>
              {serviceError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{serviceError}</p>}
              {serviceMessage && <p role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{serviceMessage}</p>}
            </CardContent>
          </Card>
        )}

        {hierarchyError && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{hierarchyError}</p>}
        {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
        {message && <p role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</p>}

        <Card>
          <CardHeader className="flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>{selectedDepartment ? selectedDepartment.name : 'Orari settimanali'}</CardTitle>
              <CardDescription>Disattiva una riga per conservarla senza applicarla ai nuovi turni.</CardDescription>
            </div>
            <Button type="button" onClick={save} disabled={!canEdit || saving || loading || hierarchyLoading || !departmentId}>
              {saving ? 'Salvataggio…' : 'Salva orari'}
            </Button>
          </CardHeader>
          <CardContent>
            {!canEdit && <p className="mb-4 rounded-xl bg-stone-50 px-4 py-3 text-sm text-stone-600">Puoi consultare gli orari, ma la modifica richiede il permesso turni.edit.</p>}
            {!departmentId ? (
              <p className="py-8 text-center text-sm text-stone-500">Seleziona un reparto per configurare gli orari.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                      <th className="border-b border-stone-200 px-2 py-3">Giorno</th>
                      {schedulingServices.map(service => (
                        <th key={service.id} className="border-b border-stone-200 px-2 py-3">{service.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {WEEKDAYS.map((day, weekday) => (
                      <tr key={day}>
                        <th className="border-b border-stone-100 px-2 py-3 text-left font-semibold text-brand-navy">{day}</th>
                        {schedulingServices.map(service => {
                          const key = slotKey(weekday, service.id);
                          const slot = { ...emptySlot(), ...(slots[key] || {}) };
                          return (
                            <td key={service.id} className="border-b border-stone-100 px-2 py-3 align-top">
                              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                                <Input aria-label={`${day} ${service.name} inizio`} type="time" value={slot.start_time} disabled={!canEdit} onChange={event => updateSlot(weekday, service.id, 'start_time', event.target.value)} />
                                <Input aria-label={`${day} ${service.name} fine`} type="time" value={slot.end_time} disabled={!canEdit} onChange={event => updateSlot(weekday, service.id, 'end_time', event.target.value)} />
                                <Input aria-label={`${day} ${service.name} pausa`} type="number" min="0" value={slot.break_minutes} disabled={!canEdit} onChange={event => updateSlot(weekday, service.id, 'break_minutes', event.target.value)} className="w-16" />
                              </div>
                              <label className="mt-2 flex items-center gap-2 text-xs text-stone-600">
                                <input type="checkbox" checked={slot.active} disabled={!canEdit || !slot.start_time || !slot.end_time} onChange={event => updateSlot(weekday, service.id, 'active', event.target.checked)} className="h-4 w-4 accent-brand-green" />
                                Attivo
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}